import { useState, useEffect } from "react";
import { T } from "./theme";
import {
  Settings, User, Brain, RefreshCw, Bell, Building2, Info,
  Save, Check, AlertTriangle, Clock, Database,
  Mail, ChevronRight,
} from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Button from "./components/Button";
import Input from "./components/Input";
import Badge from "./components/Badge";

// ─── Helpers ─────────────────────────────────────────────────────────

function SectionTitle({ icon: Icon, title, description }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${T.accent}12`, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={18} color={T.accent} />
      </div>
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>{title}</h3>
        {description && <p style={{ fontSize: 12, color: T.textLow, marginTop: 2 }}>{description}</p>}
      </div>
    </div>
  );
}

function FieldRow({ label, description, children }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "16px 0", borderBottom: `1px solid ${T.border}`, gap: 24,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.textHi, fontFamily: T.fontBody }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: T.textLow, marginTop: 2 }}>{description}</div>}
      </div>
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
        background: value ? T.accent : "#D1D5DB",
        position: "relative", transition: T.transitionFast, flexShrink: 0,
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 9, background: "#fff",
        position: "absolute", top: 3,
        left: value ? 23 : 3,
        transition: T.transitionFast,
        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      }} />
    </button>
  );
}

function SelectField({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: "8px 12px", fontSize: 13, fontFamily: T.fontBody, fontWeight: 500,
        color: T.textHi, background: T.bgInput, border: `1px solid ${T.border}`,
        borderRadius: T.radiusSm, outline: "none", cursor: "pointer",
        minWidth: 200,
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Toast({ message, type = "success", onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 9999,
      padding: "12px 20px", borderRadius: T.radiusSm,
      background: type === "success" ? T.green : T.red, color: "#fff",
      fontSize: 13, fontWeight: 600, fontFamily: T.fontBody,
      boxShadow: T.shadowLg, display: "flex", alignItems: "center", gap: 8,
      animation: "fadeSlideIn 0.3s ease",
    }}>
      {type === "success" ? <Check size={16} /> : <AlertTriangle size={16} />}
      {message}
    </div>
  );
}

// ─── Tabs Menu ───────────────────────────────────────────────────────

const TABS = [
  { key: "perfil", label: "Perfil", icon: User },
  { key: "ia", label: "Inteligência Artificial", icon: Brain },
  { key: "sync", label: "Sincronização", icon: RefreshCw },
  { key: "notificacoes", label: "Notificações", icon: Bell },
  { key: "unidades", label: "Unidades", icon: Building2 },
  { key: "sobre", label: "Sobre", icon: Info },
];

// ─── Main Component ─────────────────────────────────────────────────

export default function Configuracoes() {
  const [tab, setTab] = useState("perfil");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Profile state
  const [profile, setProfile] = useState({ nome: "", email: "", avatar: "" });

  // Preferences state
  const [prefs, setPrefs] = useState({
    geminiModel: "gemini-2.0-flash-lite",
    fallbackEnabled: true,
    npsAlertThreshold: 30,
    npsAlertEnabled: false,
    hospitalPadrao: "",
  });

  // Sync info
  const [syncInfo, setSyncInfo] = useState({
    ultimoSync: null, hospitaisVinculados: [], unidade: "", totalDepoimentos: 0,
  });

  // System info
  const [sysInfo, setSysInfo] = useState({ versao: "", nome: "", descricao: "", contato: "", ambiente: "" });

  useEffect(() => {
    Promise.all([
      fetch("/api/settings/profile", { credentials: "include" }).then(r => r.json()),
      fetch("/api/settings/preferences", { credentials: "include" }).then(r => r.json()),
      fetch("/api/settings/sync-info", { credentials: "include" }).then(r => r.json()),
      fetch("/api/settings/system-info", { credentials: "include" }).then(r => r.json()),
    ])
      .then(([prof, pref, sync, sys]) => {
        if (prof.nome) setProfile({ nome: prof.nome, email: prof.email, avatar: prof.avatar || "" });
        if (!pref.error) setPrefs(p => ({ ...p, ...pref }));
        if (!sync.error) setSyncInfo(sync);
        if (!sys.error) setSysInfo(sys);
      })
      .catch(e => console.error("Settings load:", e.message))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (message, type = "success") => setToast({ message, type });

  const saveProfile = async () => {
    setSaving(true);
    try {
      const r = await fetch("/api/settings/profile", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: profile.nome, email: profile.email, avatar: profile.avatar }),
      });
      const d = await r.json();
      if (!r.ok) return showToast(d.error || "Erro ao salvar.", "error");
      showToast("Perfil atualizado com sucesso!");
    } catch {
      showToast("Erro de conexão.", "error");
    } finally {
      setSaving(false);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      const r = await fetch("/api/settings/preferences", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(prefs),
      });
      const d = await r.json();
      if (!r.ok) return showToast(d.error || "Erro ao salvar.", "error");
      showToast("Preferências salvas!");
    } catch {
      showToast("Erro de conexão.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoading />;

  return (
    <PageContainer maxWidth={1100}>
      <PageHeader icon={Settings} title="Configurações" description="Gerencie seu perfil, preferências e integrações" />

      <div style={{ display: "flex", gap: 24 }}>
        {/* Sidebar Tabs */}
        <Card style={{ width: 240, padding: "12px", flexShrink: 0, alignSelf: "flex-start", position: "sticky", top: 32 }}>
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 12px", borderRadius: T.radiusSm, border: "none",
                  background: active ? T.accentDim : "transparent",
                  color: active ? T.accent : T.textMid,
                  fontSize: 13, fontWeight: active ? 600 : 500, fontFamily: T.fontBody,
                  cursor: "pointer", textAlign: "left", transition: T.transitionFast,
                }}
              >
                <t.icon size={16} />
                {t.label}
                {active && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
              </button>
            );
          })}
        </Card>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* ─── Perfil ─── */}
          {tab === "perfil" && (
            <Card>
              <SectionTitle icon={User} title="Perfil do Usuário" description="Edite suas informações pessoais" />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <Input label="Nome" value={profile.nome} onChange={e => setProfile(p => ({ ...p, nome: e.target.value }))} placeholder="Seu nome" />
                <Input label="Email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} placeholder="seu@email.com" icon={Mail} />
              </div>

              <Input label="Avatar (iniciais)" value={profile.avatar} onChange={e => setProfile(p => ({ ...p, avatar: e.target.value.toUpperCase().slice(0, 2) }))} placeholder="AB" style={{ maxWidth: 120, textAlign: "center", fontWeight: 700, letterSpacing: "0.05em" }} />

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                <Button icon={Save} onClick={saveProfile} loading={saving}>Salvar Perfil</Button>
              </div>
            </Card>
          )}

          {/* ─── IA ─── */}
          {tab === "ia" && (
            <Card>
              <SectionTitle icon={Brain} title="Inteligência Artificial" description="Configure o modelo e comportamento da IA" />

              <FieldRow label="Modelo Gemini" description="Modelo usado para análises, rankings e respostas">
                <SelectField
                  value={prefs.geminiModel}
                  onChange={v => setPrefs(p => ({ ...p, geminiModel: v }))}
                  options={[
                    { value: "gemini-3.1-flash-lite-preview", label: "Gemini 3.1 Flash Lite (preview)" },
                    { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite (rápido)" },
                    { value: "gemini-3.1-flash-preview", label: "Gemini 3.1 Flash (preview)" },
                    { value: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (preview)" },
                  ]}
                />
              </FieldRow>

              <FieldRow label="Fallback Automático" description="Se o modelo principal estiver indisponível, tenta outro automaticamente">
                <Toggle value={prefs.fallbackEnabled} onChange={v => setPrefs(p => ({ ...p, fallbackEnabled: v }))} />
              </FieldRow>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                <Button icon={Save} onClick={savePrefs} loading={saving}>Salvar Preferências</Button>
              </div>
            </Card>
          )}

          {/* ─── Sincronização ─── */}
          {tab === "sync" && (
            <Card>
              <SectionTitle icon={RefreshCw} title="Sincronização" description="Status da conexão com o HFocus" />

              <FieldRow label="Última Sincronização" description="Data e hora do último sync com o HFocus">
                <Badge color={syncInfo.ultimoSync ? T.green : T.amber} dot>
                  {syncInfo.ultimoSync
                    ? new Date(syncInfo.ultimoSync).toLocaleString("pt-BR")
                    : "Nunca sincronizado"}
                </Badge>
              </FieldRow>

              <FieldRow label="Total de Depoimentos" description="Registros importados no banco de dados">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Database size={16} color={T.accent} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>
                    {(syncInfo.totalDepoimentos || 0).toLocaleString("pt-BR")}
                  </span>
                </div>
              </FieldRow>

              <FieldRow label="Unidade" description="Instituição vinculada à sua conta">
                <span style={{ fontSize: 14, fontWeight: 600, color: T.textHi }}>{syncInfo.unidade || "—"}</span>
              </FieldRow>

              {syncInfo.hospitaisVinculados?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textLow, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Áreas Sincronizadas
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {syncInfo.hospitaisVinculados.map((h, i) => (
                      <Badge key={i} color={T.accent}>{h}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* ─── Notificações ─── */}
          {tab === "notificacoes" && (
            <Card>
              <SectionTitle icon={Bell} title="Notificações" description="Configure alertas e notificações do sistema" />

              <FieldRow label="Alerta de NPS Crítico" description="Receba um alerta quando o NPS ficar abaixo do threshold">
                <Toggle value={prefs.npsAlertEnabled} onChange={v => setPrefs(p => ({ ...p, npsAlertEnabled: v }))} />
              </FieldRow>

              {prefs.npsAlertEnabled && (
                <FieldRow label="Threshold de NPS" description="Valor abaixo do qual o alerta é disparado">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="range" min={-100} max={100} step={5}
                      value={prefs.npsAlertThreshold}
                      onChange={e => setPrefs(p => ({ ...p, npsAlertThreshold: Number(e.target.value) }))}
                      style={{ width: 120, accentColor: T.accent }}
                    />
                    <span style={{
                      fontSize: 14, fontWeight: 700, fontFamily: T.fontMono,
                      color: prefs.npsAlertThreshold < 0 ? T.red : prefs.npsAlertThreshold < 50 ? T.amber : T.green,
                      minWidth: 40, textAlign: "right",
                    }}>
                      {prefs.npsAlertThreshold}
                    </span>
                  </div>
                </FieldRow>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                <Button icon={Save} onClick={savePrefs} loading={saving}>Salvar Notificações</Button>
              </div>
            </Card>
          )}

          {/* ─── Unidades ─── */}
          {tab === "unidades" && (
            <Card>
              <SectionTitle icon={Building2} title="Unidades e Hospitais" description="Hospital vinculado à sua conta" />

              {/* Hospital do usuário */}
              <div style={{
                display: "flex", alignItems: "center", gap: 16,
                padding: "20px 24px", borderRadius: T.radiusSm,
                background: T.bgBase, border: `1px solid ${T.border}`, marginBottom: 20,
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: T.gradientAccent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 4px 12px ${T.primaryGlow}`,
                }}>
                  <Building2 size={22} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>
                    {syncInfo.unidade || profile.nome || "Sua Instituição"}
                  </div>
                  <div style={{ fontSize: 12, color: T.textLow, marginTop: 2 }}>Conectado via HFocus</div>
                </div>
                <Badge color={T.green} dot>Ativo</Badge>
              </div>

              {/* Áreas sincronizadas */}
              {syncInfo.hospitaisVinculados?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.textLow, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Áreas Sincronizadas
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {syncInfo.hospitaisVinculados.map((h, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 14px", borderRadius: T.radiusSm,
                        background: T.bgCard, border: `1px solid ${T.border}`,
                      }}>
                        <div style={{
                          width: 6, height: 6, borderRadius: 3, background: T.green, flexShrink: 0,
                        }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: T.textHi }}>{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* ─── Sobre ─── */}
          {tab === "sobre" && (
            <Card>
              <SectionTitle icon={Info} title="Sobre o Sistema" description="Informações da plataforma" />

              <div style={{
                padding: 24, background: T.bgBase, borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, marginBottom: 20,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 14, background: T.gradientAccent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: `0 4px 12px ${T.primaryGlow}`,
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: T.textHi, fontFamily: T.fontTitle, letterSpacing: "-0.03em" }}>
                      {sysInfo.nome || "TrueNPS"}
                    </div>
                    <div style={{ fontSize: 13, color: T.textLow, marginTop: 2 }}>
                      {sysInfo.descricao || "Plataforma de Gestão de Experiência do Paciente"}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { label: "Versão", value: sysInfo.versao || "1.0.0", icon: Info },
                    { label: "Ambiente", value: sysInfo.ambiente || "production", icon: Database },
                    { label: "Contato", value: sysInfo.contato || "—", icon: Mail },
                    { label: "Uptime", value: "Online", icon: Clock },
                  ].map(item => (
                    <div key={item.label} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "12px 14px", borderRadius: T.radiusSm,
                      background: T.bgCard, border: `1px solid ${T.border}`,
                    }}>
                      <item.icon size={14} color={T.textLow} />
                      <div>
                        <div style={{ fontSize: 11, color: T.textLow, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.textHi, marginTop: 1 }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </PageContainer>
  );
}
